/* ── Kangaroo Kourt: data model, table I/O, judge API and leaderboard math ── */

import { PHOTO_NAME_RE } from './photos';
import type { RosterEntry } from './roster';
import { ODATA_HEADERS, odataQuote, type TablesConfig } from './storage';

// Dev runs against the production table (the SAS is public config) and rows
// can never be deleted, so a test run can keep its rows in their own
// partitions: set VITE_KOURT_PARTITION_SUFFIX=-dev (in .env.local) and give
// the server the same KOURT_PARTITION_SUFFIX. Unset, dev and prod read the
// same real rows — testing against real data is the default.
const SUFFIX: string = import.meta.env.VITE_KOURT_PARTITION_SUFFIX ?? '';
export const KOURT_PARTITION = {
  case: `case${SUFFIX}`,
  ruling: `ruling${SUFFIX}`,
  /** "Serve me by text" opt-ins — roster name + mobile, newest wins. */
  phone: `phone${SUFFIX}`,
  /** Money handed over. Written only by the server, on the judge's order. */
  payment: `payment${SUFFIX}`,
  /** A ruling struck from the totals. RowKey is the case key, so once only. */
  vacate: `vacate${SUFFIX}`,
  /** Posts the moderator hid from /admin → Reports. RowKey is the message's or case's key. */
  hidden: `hidden${SUFFIX}`,
} as const;

/** What a hidden complaint shows in place of its text. */
export const REMOVED_TEXT = '[Removed by the moderator]';

/** The judge's passcode, kept for the tab only — never localStorage. */
export const JUDGE_KEY = 'tlh-kourt-passcode';
export const THE_BENCH = 'The Bench';

export type CaseKind = 'fine' | 'bonus';
export type Verdict = 'fined' | 'bonus' | 'dismissed';

/** A complaint on the docket. Anyone registered can file one. */
export interface KourtCase {
  rowKey: string;
  defendant: string;
  kind: CaseKind;
  /** What the filer recommends, in whole dollars. */
  amount: number;
  offense: string;
  filedBy: string;
  filedAt: string;
  /** Exhibit A: a file name in the photos blob folder (`<rowKey>.jpg`). */
  photoName?: string;
}

/** The Bench's decision. One per case, ever — the row key is the case key. */
export interface KourtRuling {
  caseKey: string;
  defendant: string;
  verdict: Verdict;
  /** Final amount. Zero when dismissed. */
  amount: number;
  opinion: string;
  ruledAt: string;
  /** A fine's summons to appear, a PDF in the photos blob folder. */
  summonsName?: string;
}

const SUMMONS_NAME_RE = /^[A-Za-z0-9._-]{1,80}\.pdf$/;

const LIMITS = { defendant: 60, offense: 300, opinion: 1000, filedBy: 40, amount: 500 } as const;

/* ── Sanitizers (accept camelCase WS payloads and PascalCase table rows) ── */

type Raw = Record<string, unknown>;

function pick(e: Raw, camel: string, pascal: string): string | null {
  const v = typeof e[camel] === 'string' ? e[camel] : typeof e[pascal] === 'string' ? e[pascal] : null;
  return v as string | null;
}

function pickInt(e: Raw, camel: string, pascal: string): number | null {
  const v = e[camel] ?? e[pascal];
  const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : NaN;
  return Number.isInteger(n) && n >= 0 && n <= LIMITS.amount ? n : null;
}

export function toCase(raw: unknown): KourtCase | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const e = raw as Raw;
  const rowKey = pick(e, 'rowKey', 'RowKey');
  const defendant = pick(e, 'defendant', 'Defendant');
  const kind = pick(e, 'kind', 'Kind');
  const amount = pickInt(e, 'amount', 'Amount');
  const offense = pick(e, 'offense', 'Offense');
  const filedBy = pick(e, 'filedBy', 'FiledBy');
  const filedAt = pick(e, 'filedAt', 'FiledAt') ?? '';
  let photoName = pick(e, 'photoName', 'PhotoName') ?? '';
  if (photoName && !PHOTO_NAME_RE.test(photoName)) photoName = '';
  if (!rowKey || !defendant || (kind !== 'fine' && kind !== 'bonus') || amount === null) return null;
  if (!offense || !offense.trim() || !filedBy) return null;
  return {
    rowKey: rowKey.slice(0, 64),
    defendant: defendant.slice(0, LIMITS.defendant),
    kind,
    amount,
    offense: offense.slice(0, LIMITS.offense),
    filedBy: filedBy.slice(0, LIMITS.filedBy),
    filedAt,
    ...(photoName ? { photoName } : {}),
  };
}

export function toRuling(raw: unknown): KourtRuling | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const e = raw as Raw;
  const caseKey = pick(e, 'caseKey', 'RowKey');
  const defendant = pick(e, 'defendant', 'Defendant');
  const verdict = pick(e, 'verdict', 'Verdict');
  const amount = pickInt(e, 'amount', 'Amount');
  const opinion = pick(e, 'opinion', 'Opinion');
  const ruledAt = pick(e, 'ruledAt', 'RuledAt') ?? '';
  let summonsName = pick(e, 'summonsName', 'SummonsName') ?? '';
  if (summonsName && !SUMMONS_NAME_RE.test(summonsName)) summonsName = '';
  if (!caseKey || !defendant || amount === null || !opinion || !opinion.trim()) return null;
  if (verdict !== 'fined' && verdict !== 'bonus' && verdict !== 'dismissed') return null;
  return {
    caseKey: caseKey.slice(0, 64),
    defendant: defendant.slice(0, LIMITS.defendant),
    verdict,
    amount: verdict === 'dismissed' ? 0 : amount,
    opinion: opinion.slice(0, LIMITS.opinion),
    ruledAt,
    ...(summonsName ? { summonsName } : {}),
  };
}

/** Money a player has actually handed over, recorded by the Bench. */
export interface KourtPayment {
  rowKey: string;
  player: string;
  amount: number;
  note: string;
  paidAt: string;
}

/**
 * An order striking a ruling from the totals. Nothing is edited or deleted —
 * the ruling stays on the record, stamped VACATED — and because the row key
 * is the case key, a ruling can be vacated exactly once and never reinstated.
 */
export interface KourtVacate {
  caseKey: string;
  defendant: string;
  reason: string;
  vacatedAt: string;
}

export function toPayment(raw: unknown): KourtPayment | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const e = raw as Raw;
  const rowKey = pick(e, 'rowKey', 'RowKey');
  const player = pick(e, 'player', 'Player');
  const amount = pickInt(e, 'amount', 'Amount');
  if (!rowKey || !player || amount === null || amount < 1) return null;
  return {
    rowKey: rowKey.slice(0, 64),
    player: player.slice(0, LIMITS.defendant),
    amount,
    note: (pick(e, 'note', 'Note') ?? '').slice(0, 200),
    paidAt: pick(e, 'paidAt', 'PaidAt') ?? '',
  };
}

export function toVacate(raw: unknown): KourtVacate | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const e = raw as Raw;
  const caseKey = pick(e, 'caseKey', 'RowKey');
  const reason = pick(e, 'reason', 'Reason');
  if (!caseKey || !reason || !reason.trim()) return null;
  return {
    caseKey: caseKey.slice(0, 64),
    defendant: (pick(e, 'defendant', 'Defendant') ?? '').slice(0, LIMITS.defendant),
    reason: reason.slice(0, LIMITS.offense),
    vacatedAt: pick(e, 'vacatedAt', 'VacatedAt') ?? '',
  };
}

/* ── Table I/O (browser side, read + add SAS) ─────────────────────────── */

/** One partition, whole. A season of fines is nowhere near the 1000-row
 *  page limit, so continuation tokens are deliberately ignored. */
async function fetchPartition(tables: TablesConfig, partition: string): Promise<unknown[] | null> {
  try {
    const filter = `PartitionKey eq '${odataQuote(partition)}'`;
    const response = await fetch(
      `${tables.endpoint}/messages()?$filter=${encodeURIComponent(filter)}&${tables.messagesSas}`,
      { headers: ODATA_HEADERS },
    );
    if (!response.ok) return null;
    const body = await response.json();
    return Array.isArray(body?.value) ? body.value : null;
  } catch {
    return null;
  }
}

/** RowKeys the moderator has hidden (messages and complaints); empty when unreadable. */
export async function fetchHiddenKeys(tables: TablesConfig): Promise<Set<string>> {
  const rows = await fetchPartition(tables, KOURT_PARTITION.hidden);
  return new Set(
    (rows ?? []).map((r) => (r as { RowKey?: unknown }).RowKey).filter((k): k is string => typeof k === 'string'),
  );
}

export async function fetchCases(tables: TablesConfig): Promise<KourtCase[] | null> {
  const [rows, hidden] = await Promise.all([fetchPartition(tables, KOURT_PARTITION.case), fetchHiddenKeys(tables)]);
  if (!rows) return null;
  return rows
    .map(toCase)
    .filter((c): c is KourtCase => c !== null)
    .map((c) => (hidden.has(c.rowKey) ? { ...c, offense: REMOVED_TEXT, photoName: undefined } : c));
}

export async function fetchRulings(tables: TablesConfig): Promise<KourtRuling[] | null> {
  const rows = await fetchPartition(tables, KOURT_PARTITION.ruling);
  return rows ? rows.map(toRuling).filter((r): r is KourtRuling => r !== null) : null;
}

export async function fetchPayments(tables: TablesConfig): Promise<KourtPayment[] | null> {
  const rows = await fetchPartition(tables, KOURT_PARTITION.payment);
  return rows ? rows.map(toPayment).filter((x): x is KourtPayment => x !== null) : null;
}

export async function fetchVacates(tables: TablesConfig): Promise<KourtVacate[] | null> {
  const rows = await fetchPartition(tables, KOURT_PARTITION.vacate);
  return rows ? rows.map(toVacate).filter((x): x is KourtVacate => x !== null) : null;
}

export async function insertCase(tables: TablesConfig, c: KourtCase): Promise<boolean> {
  try {
    const entity: Record<string, string | number> = {
      PartitionKey: KOURT_PARTITION.case,
      RowKey: c.rowKey,
      Defendant: c.defendant,
      Kind: c.kind,
      Amount: c.amount,
      Offense: c.offense,
      FiledBy: c.filedBy,
      FiledAt: c.filedAt,
    };
    if (c.photoName) entity.PhotoName = c.photoName;
    const response = await fetch(`${tables.endpoint}/messages?${tables.messagesSas}`, {
      method: 'POST',
      headers: { ...ODATA_HEADERS, 'Content-Type': 'application/json', Prefer: 'return-no-content' },
      body: JSON.stringify(entity),
    });
    return response.ok;
  } catch {
    return false;
  }
}

/* ── "This is me": ties a registered email to a roster name ─────────────
   Registration knows an email and a nickname; the judge fines a roster name.
   This row is the only link between the two, and it is what lets the Kourt
   email (and, with a mobile number, text) the defendant alone. */

export interface PhoneOptIn {
  rowKey: string;
  email: string;
  rosterName: string;
  /** Optional. E.164, US only: +1 and ten digits — or '' for email only. */
  phone: string;
}

/** Ten US digits (optionally with a leading 1) → +1XXXXXXXXXX, else null. */
export function normalizePhone(input: string): string | null {
  const digits = input.replace(/\D/g, '');
  const ten = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  return /^[2-9]\d{9}$/.test(ten) ? `+1${ten}` : null;
}

/** +17705551212 → (770) 555-1212 */
export function formatPhone(e164: string): string {
  const d = e164.replace(/\D/g, '').slice(-10);
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : e164;
}

function toPhoneOptIn(raw: unknown): PhoneOptIn | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const e = raw as Raw;
  const rowKey = pick(e, 'rowKey', 'RowKey');
  const email = pick(e, 'email', 'Email');
  const rosterName = pick(e, 'rosterName', 'RosterName');
  const phone = pick(e, 'phone', 'Phone');
  if (!rowKey || !email || !rosterName) return null;
  return {
    rowKey,
    email,
    rosterName: rosterName.slice(0, LIMITS.defendant),
    phone: phone && /^\+1\d{10}$/.test(phone) ? phone : '',
  };
}

/** The newest opt-in filed under this email, or null. Rows are add-only, so
 *  changing a number means filing a fresh row; the inverted-timestamp key
 *  sorts the latest first. */
export async function fetchPhoneOptIn(tables: TablesConfig, email: string): Promise<PhoneOptIn | null> {
  try {
    const filter = `PartitionKey eq '${odataQuote(KOURT_PARTITION.phone)}' and Email eq '${odataQuote(email)}'`;
    const response = await fetch(
      `${tables.endpoint}/messages()?$filter=${encodeURIComponent(filter)}&${tables.messagesSas}`,
      { headers: ODATA_HEADERS },
    );
    if (!response.ok) return null;
    const body = await response.json();
    const rows = (Array.isArray(body?.value) ? body.value : [])
      .map(toPhoneOptIn)
      .filter((r: PhoneOptIn | null): r is PhoneOptIn => r !== null)
      .sort((a: PhoneOptIn, b: PhoneOptIn) => a.rowKey.localeCompare(b.rowKey));
    return rows[0] ?? null;
  } catch {
    return null;
  }
}

export async function insertPhoneOptIn(tables: TablesConfig, optIn: PhoneOptIn): Promise<boolean> {
  try {
    const response = await fetch(`${tables.endpoint}/messages?${tables.messagesSas}`, {
      method: 'POST',
      headers: { ...ODATA_HEADERS, 'Content-Type': 'application/json', Prefer: 'return-no-content' },
      body: JSON.stringify({
        PartitionKey: KOURT_PARTITION.phone,
        RowKey: optIn.rowKey,
        Email: optIn.email,
        RosterName: optIn.rosterName,
        ...(optIn.phone ? { Phone: optIn.phone } : {}),
      }),
    });
    return response.ok;
  } catch {
    return false;
  }
}

/** Union by key; incoming wins. Order is left to the callers' sorts. */
export function mergeByKey<T>(current: T[], incoming: T[], key: (item: T) => string): T[] {
  const byKey = new Map<string, T>();
  for (const item of [...current, ...incoming]) byKey.set(key(item), item);
  return [...byKey.values()];
}

/* ── The judge's API (/api/kourt, passcode checked server-side) ────────── */

export type KourtResult<T> = ({ ok: true } & T) | { ok: false; status: number; reason: string };

async function callKourt<T>(body: Record<string, unknown>): Promise<KourtResult<T>> {
  try {
    const response = await fetch('/api/kourt', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = (await response.json().catch(() => ({}))) as Record<string, unknown>;
    if (response.ok && data.ok === true) return { ...(data as T), ok: true };
    return {
      ok: false,
      status: response.status,
      reason: typeof data.reason === 'string' ? data.reason : `http ${response.status}`,
    };
  } catch {
    return { ok: false, status: 0, reason: 'unreachable' };
  }
}

/** Whether a gavel has been issued (passcode + storage configured server-side). */
export async function kourtStatus(): Promise<boolean> {
  try {
    const response = await fetch('/api/kourt');
    if (!response.ok) return false;
    const data = (await response.json()) as { configured?: unknown };
    return data.configured === true;
  } catch {
    return false;
  }
}

export function verifyJudge(passcode: string): Promise<KourtResult<object>> {
  return callKourt({ action: 'verify', passcode });
}

export interface RulingInput {
  caseKey: string;
  verdict: Verdict;
  amount: number;
  opinion: string;
}

export function ruleOnCase(
  passcode: string,
  input: RulingInput,
): Promise<KourtResult<{ ruling: KourtRuling; emailed: boolean; noAddress?: boolean; texted: boolean }>> {
  return callKourt({ action: 'rule', passcode, ...input });
}

export interface SuaSponteInput {
  defendant: string;
  verdict: 'fined' | 'bonus';
  amount: number;
  offense: string;
  opinion: string;
}

export function ruleSuaSponte(
  passcode: string,
  input: SuaSponteInput,
): Promise<KourtResult<{ case: KourtCase; ruling: KourtRuling; emailed: boolean; noAddress?: boolean; texted: boolean }>> {
  return callKourt({ action: 'sua-sponte', passcode, ...input });
}

export function recordPayment(
  passcode: string,
  input: { player: string; amount: number; note: string },
): Promise<KourtResult<{ payment: KourtPayment }>> {
  return callKourt({ action: 'payment', passcode, ...input });
}

export function vacateRuling(
  passcode: string,
  input: { caseKey: string; reason: string },
): Promise<KourtResult<{ vacate: KourtVacate; emailed: boolean; noAddress?: boolean }>> {
  return callKourt({ action: 'vacate', passcode, ...input });
}

/* ── Leaderboard math (pure) ───────────────────────────────────────────── */

export interface Standing {
  name: string;
  /** Fines minus credits minus what he has paid. Positive means he still owes. */
  owed: number;
  /** Cash handed over and recorded by the Bench. */
  paid: number;
  vacatedCount: number;
  fineCount: number;
  fineTotal: number;
  bonusCount: number;
  bonusTotal: number;
  dismissedCount: number;
  onRoster: boolean;
  rank: number;
  title: string;
}

export function rankTitle(rank: number, owed: number, hasHistory: boolean): string {
  if (owed < 0) return 'Model Citizen';
  if (owed === 0) return hasHistory ? 'Square With the Kourt' : 'Clean Record (for now)';
  if (rank === 1) return 'Public Enemy No. 1';
  if (rank === 2) return 'Menace to Society';
  if (rank === 3) return 'Habitual Offender';
  return 'Person of Interest';
}

/** Everyone on the roster plus anyone the Kourt has ever ruled on, ranked by
 *  what they owe. Names are exact strings — no fuzzy matching, ever. */
export function computeStandings(
  rulings: KourtRuling[],
  roster: RosterEntry[],
  payments: KourtPayment[] = [],
  vacates: KourtVacate[] = [],
): Standing[] {
  const struck = new Set(vacates.map((v) => v.caseKey));
  const rosterNames = new Set(roster.map((p) => p.name));
  const tally = new Map<string, Standing>();
  const blank = (name: string): Standing => ({
    name,
    owed: 0,
    paid: 0,
    vacatedCount: 0,
    fineCount: 0,
    fineTotal: 0,
    bonusCount: 0,
    bonusTotal: 0,
    dismissedCount: 0,
    onRoster: rosterNames.has(name),
    rank: 0,
    title: '',
  });
  for (const name of rosterNames) tally.set(name, blank(name));
  for (const r of rulings) {
    const s = tally.get(r.defendant) ?? blank(r.defendant);
    if (struck.has(r.caseKey)) {
      // Vacated: on the record, off the books.
      s.vacatedCount += 1;
      tally.set(r.defendant, s);
      continue;
    }
    if (r.verdict === 'fined') {
      s.fineCount += 1;
      s.fineTotal += r.amount;
    } else if (r.verdict === 'bonus') {
      s.bonusCount += 1;
      s.bonusTotal += r.amount;
    } else {
      s.dismissedCount += 1;
    }
    tally.set(r.defendant, s);
  }
  for (const pay of payments) {
    const s = tally.get(pay.player) ?? blank(pay.player);
    s.paid += pay.amount;
    tally.set(pay.player, s);
  }
  const standings = [...tally.values()].map((s) => ({ ...s, owed: s.fineTotal - s.bonusTotal - s.paid }));
  standings.sort(
    (a, b) => b.owed - a.owed || b.fineCount - a.fineCount || a.name.localeCompare(b.name),
  );
  return standings.map((s, i) => ({
    ...s,
    rank: i + 1,
    title: rankTitle(
      i + 1,
      s.owed,
      s.fineCount + s.bonusCount + s.dismissedCount + s.vacatedCount > 0 || s.paid > 0,
    ),
  }));
}

export interface MojitoFund {
  /** Fines that still stand. */
  gross: number;
  credits: number;
  /** What the team owes the fund in all: fines less credits. */
  net: number;
  /** Cash the Bench has actually recorded as paid. */
  collected: number;
  outstanding: number;
}

export function mojitoFund(
  rulings: KourtRuling[],
  payments: KourtPayment[] = [],
  vacates: KourtVacate[] = [],
): MojitoFund {
  const struck = new Set(vacates.map((v) => v.caseKey));
  let gross = 0;
  let credits = 0;
  for (const r of rulings) {
    if (struck.has(r.caseKey)) continue;
    if (r.verdict === 'fined') gross += r.amount;
    else if (r.verdict === 'bonus') credits += r.amount;
  }
  const collected = payments.reduce((n, pay) => n + pay.amount, 0);
  const net = gross - credits;
  return { gross, credits, net, collected, outstanding: net - collected };
}

/** Complaints nobody has ruled on, newest first (row keys are inverted timestamps). */
export function pendingDocket(cases: KourtCase[], rulings: KourtRuling[]): KourtCase[] {
  const ruled = new Set(rulings.map((r) => r.caseKey));
  return cases.filter((c) => !ruled.has(c.rowKey)).sort((a, b) => a.rowKey.localeCompare(b.rowKey));
}

export interface RecordEntry {
  ruling: KourtRuling;
  /** Missing only if the case row failed to load; the ruling still stands. */
  filed?: KourtCase;
  /** Set when the Bench has struck this ruling from the totals. */
  vacated?: KourtVacate;
}

/** Every ruling, newest first, paired with the complaint it answered. */
export function kourtRecord(
  cases: KourtCase[],
  rulings: KourtRuling[],
  vacates: KourtVacate[] = [],
): RecordEntry[] {
  const byKey = new Map(cases.map((c) => [c.rowKey, c]));
  const struck = new Map(vacates.map((v) => [v.caseKey, v]));
  return rulings
    .map((ruling) => ({ ruling, filed: byKey.get(ruling.caseKey), vacated: struck.get(ruling.caseKey) }))
    .sort((a, b) => b.ruling.ruledAt.localeCompare(a.ruling.ruledAt));
}

export function rapSheet(
  name: string,
  cases: KourtCase[],
  rulings: KourtRuling[],
  vacates: KourtVacate[] = [],
): RecordEntry[] {
  return kourtRecord(cases, rulings, vacates).filter((e) => e.ruling.defendant === name);
}

/* ── Kourt business around a match: Monday before through Sunday after ─── */

const isoDay = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/**
 * The stretch of days whose Kourt business belongs to a match: match day
 * itself through the day before the next match. Only what the Bench handed
 * down AFTER a match counts as that match's fines — the Monday and Tuesday
 * write-ups, not anything from the week leading up to it. The last match
 * of the season runs on open-ended.
 */
export function matchWeekWindow(
  matchIso: string,
  nextMatchIso?: string,
): { start: string; end: string } {
  const shift = (iso: string, days: number) => {
    const [y, m, d] = iso.split('-').map(Number);
    return isoDay(new Date(y, m - 1, d + days));
  };
  return {
    start: matchIso,
    end: nextMatchIso ? shift(nextMatchIso, -1) : '9999-12-31',
  };
}

export interface KourtWeek {
  entries: RecordEntry[];
  fines: number;
  fineTotal: number;
  credits: number;
  creditTotal: number;
  dismissed: number;
  vacated: number;
}

/** Every ruling entered in a window of local days (inclusive), newest first, with totals.
 *  Vacated rulings stay in the list, flagged, but count for nothing. */
export function kourtWeek(
  cases: KourtCase[],
  rulings: KourtRuling[],
  start: string,
  end: string,
  vacates: KourtVacate[] = [],
): KourtWeek {
  const entries = kourtRecord(cases, rulings, vacates).filter((e) => {
    const when = new Date(e.ruling.ruledAt);
    if (Number.isNaN(when.getTime())) return false;
    const day = isoDay(when);
    return day >= start && day <= end;
  });
  const week: KourtWeek = { entries, fines: 0, fineTotal: 0, credits: 0, creditTotal: 0, dismissed: 0, vacated: 0 };
  for (const { ruling, vacated } of entries) {
    if (vacated) {
      week.vacated += 1;
    } else if (ruling.verdict === 'fined') {
      week.fines += 1;
      week.fineTotal += ruling.amount;
    } else if (ruling.verdict === 'bonus') {
      week.credits += 1;
      week.creditTotal += ruling.amount;
    } else {
      week.dismissed += 1;
    }
  }
  return week;
}

/** "$12", "−$3" — sign shown, never a decimal; the Kourt deals in whole dollars. */
export function formatDollars(n: number): string {
  return n < 0 ? `−$${Math.abs(n)}` : `$${n}`;
}
